import { BodyPrimary, H6 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo, ReactElement } from 'react';
import isEqual from 'react-fast-compare';

import type { SavedSearch } from '@/redux/api/saveSearch';

import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { ManageSavedSearchCard } from './ManageSavedSearchCard';
import MobileSlideMenu from './MobileSlideMenu';

type Props = {
    className?: string;
    preparedSavedSearchFilter: ReactElement;
    searches?: SavedSearch[];
    /** Every followed search matching the current filter, not just the page on screen. */
    totalCount: number;
};

const ManageSavedSearchGridComponent = (props: Props) => {
    const { className, preparedSavedSearchFilter, searches, totalCount } = props;
    const isMobile = useIsMobileLayout();
    const formatMessage = useAppFormatMessage();

    return (
        <div className={cn('w-full p-0', className)}>
            {isMobile ? (
                <div className="flex flex-row items-center justify-between border-b border-border-secondary pb-md">
                    <BodyPrimary color="secondary">
                        {totalCount === 1
                            ? formatMessage(['search_alerts', 'singleSearchTerm'])
                            : formatMessage(['search_alerts', 'countSearchTerms'], { values: { count: totalCount } })}
                    </BodyPrimary>
                    <MobileSlideMenu preparedSavedSearchFilter={preparedSavedSearchFilter} />
                </div>
            ) : (
                <div className="grid grid-cols-[auto_150px_140px_115px] border-b border-border-secondary pb-[6px]">
                    <div className="col-start-1 col-end-2 flex flex-col items-start justify-center p-0">
                        <H6>{formatMessage(['name'])}</H6>
                    </div>
                    <div className="col-start-2 col-end-3 flex items-center justify-end pl-sm text-right">
                        <H6>{formatMessage(['search_alerts', 'newInPast24Hrs'])}</H6>
                    </div>
                    <div className="col-start-3 col-end-4 flex items-center justify-end pl-sm">
                        <H6>{formatMessage(['search_alerts', 'dateAdded'])}</H6>
                    </div>
                </div>
            )}
            {searches.map((search) => (
                <ManageSavedSearchCard
                    key={search.savedSearchId}
                    search={search}
                />
            ))}
        </div>
    );
};

export const ManageSavedSearchGrid = memo<Props>(ManageSavedSearchGridComponent, isEqual);
ManageSavedSearchGrid.displayName = 'ManageSavedSearchGrid';
